import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { 
  BarChart3, Users, MousePointerClick, TrendingUp, RefreshCw,
  ChevronDown, ChevronUp, FlaskConical, Target, Eye
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import AdminHeader from './AdminHeader';
import { useAdminAuth } from '../hooks';
import { AB_TESTS } from '../context/ABTestContext';

import { apiUrl, API_URL } from '../utils/config';
import logger from '../utils/logger';

const ABTestAdmin = ({ onBack }) => {
  const { t } = useTranslation();
  const { loading: authLoading, handleLogout } = useAdminAuth();
  const [summary, setSummary] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedTest, setExpandedTest] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('admin_token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      const [summaryRes, resultsRes] = await Promise.all([
        fetch(apiUrl("ab-tests/summary"), { headers }),
        fetch(apiUrl("ab-tests/results"), { headers })
      ]);
      
      if (summaryRes.ok) {
        setSummary(await summaryRes.json());
      }
      
      if (resultsRes.ok) {
        setResults(await resultsRes.json());
      }
    } catch (err) {
      setError('Failed to load A/B test data');
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getTestInfo = (testId) => {
    return Object.values(AB_TESTS).find(t => t.id === testId) || { name: testId, description: '' };
  };

  const calculateConversionRate = (impressions, conversions) => {
    if (!impressions || impressions === 0) return '0.00';
    return ((conversions / impressions) * 100).toFixed(2);
  };

  const getWinningVariant = (testId) => {
    if (!results || !results[testId]) return null;
    
    const testResults = results[testId];
    const variants = Object.keys(testResults.impressions || {});
    
    if (variants.length === 0) return null;
    
    let winner = null;
    let highestRate = -1;
    
    variants.forEach(variant => {
      const impressions = testResults.impressions[variant]?.count || 0;
      const conversions = testResults.conversions?.filter(c => c.variant === variant)
        .reduce((sum, c) => sum + c.count, 0) || 0;
      const rate = impressions > 0 ? conversions / impressions : 0;
      
      if (rate > highestRate) {
        highestRate = rate;
        winner = variant;
      }
    });
    
    return winner;
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader onLogout={handleLogout} />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3">
              <FlaskConical className="w-8 h-8 text-purple-600" />
              <h1 className="text-3xl font-bold text-gray-900">A/B Testing Dashboard</h1>
            </div>
            <p className="text-gray-600 mt-2">Monitor and analyze your conversion experiments</p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={fetchData}
              variant="outline"
              className="flex items-center gap-2"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button onClick={onBack} variant="outline">
              Back to Dashboard
            </Button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Users</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {loading ? '...' : (summary?.totalUsers || 0).toLocaleString()}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Impressions</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {loading ? '...' : (summary?.totalImpressions || 0).toLocaleString()}
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <Eye className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Conversions</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {loading ? '...' : (summary?.totalConversions || 0).toLocaleString()}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <MousePointerClick className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Active Tests</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {loading ? '...' : (summary?.activeTests || Object.keys(AB_TESTS).length)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <FlaskConical className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Test Cards */}
        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-gray-900">Active Experiments</h2>
          
          {Object.entries(AB_TESTS).map(([key, test]) => {
            const testResults = results?.[test.id];
            const winner = getWinningVariant(test.id);
            const isExpanded = expandedTest === test.id;
            const testRate = summary?.testRates?.[test.id];
            
            return (
              <Card key={test.id} className="overflow-hidden">
                <CardHeader 
                  className="cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setExpandedTest(isExpanded ? null : test.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <Target className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{test.name}</CardTitle>
                        <CardDescription>{test.description}</CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      {testRate && (
                        <div className="text-right">
                          <p className="text-sm text-gray-500">Conversion Rate</p>
                          <p className="text-xl font-bold text-green-600">{testRate.conversionRate}%</p>
                        </div>
                      )}
                      {winner && (
                        <div className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                          Leading: {winner}
                        </div>
                      )}
                      {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                    </div>
                  </div>
                </CardHeader>
                
                {isExpanded && (
                  <CardContent className="border-t bg-gray-50">
                    <div className="pt-4">
                      {/* Variants Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        {Object.entries(test.variants).map(([variantKey, variantConfig]) => {
                          const impressions = testResults?.impressions?.[variantKey]?.count || 0;
                          const conversions = testResults?.conversions
                            ?.filter(c => c.variant === variantKey)
                            .reduce((sum, c) => sum + c.count, 0) || 0;
                          const rate = calculateConversionRate(impressions, conversions);
                          const isWinner = winner === variantKey;
                          
                          return (
                            <div 
                              key={variantKey}
                              className={`p-4 rounded-lg border-2 ${
                                isWinner ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white'
                              }`}
                            >
                              <div className="flex items-center justify-between mb-3">
                                <span className={`text-lg font-bold ${isWinner ? 'text-green-700' : 'text-gray-700'}`}>
                                  Variant {variantKey}
                                </span>
                                {isWinner && (
                                  <span className="text-xs bg-green-500 text-white px-2 py-1 rounded-full">
                                    LEADING
                                  </span>
                                )}
                              </div>
                              
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Impressions</span>
                                  <span className="font-medium">{impressions.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Conversions</span>
                                  <span className="font-medium">{conversions.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between pt-2 border-t">
                                  <span className="text-gray-700 font-medium">Conv. Rate</span>
                                  <span className={`font-bold ${isWinner ? 'text-green-600' : 'text-gray-900'}`}>
                                    {rate}%
                                  </span>
                                </div>
                              </div>
                              
                              {/* Variant Config Preview */}
                              <div className="mt-4 pt-3 border-t border-dashed">
                                <p className="text-xs text-gray-500 mb-1">Configuration:</p>
                                <pre className="text-xs bg-gray-100 p-2 rounded overflow-x-auto">
                                  {JSON.stringify(
                                    typeof variantConfig === 'object' ? variantConfig : { value: variantConfig },
                                    null, 
                                    2
                                  ).slice(0, 150)}...
                                </pre>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Conversion Events */}
                      {testResults?.conversions && testResults.conversions.length > 0 && (
                        <div>
                          <h4 className="font-medium text-gray-700 mb-3">Conversion Events</h4>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="bg-gray-100">
                                  <th className="px-4 py-2 text-left">Variant</th>
                                  <th className="px-4 py-2 text-left">Event Type</th>
                                  <th className="px-4 py-2 text-right">Count</th>
                                  <th className="px-4 py-2 text-right">Unique Users</th>
                                </tr>
                              </thead>
                              <tbody>
                                {testResults.conversions.map((conv, idx) => (
                                  <tr key={`variant-${v.variant}-${idx}`} className="border-b">
                                    <td className="px-4 py-2 font-medium">{conv.variant}</td>
                                    <td className="px-4 py-2">{conv.eventType}</td>
                                    <td className="px-4 py-2 text-right">{conv.count}</td>
                                    <td className="px-4 py-2 text-right">{conv.uniqueUsers}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                      
                      {(!testResults || Object.keys(testResults.impressions || {}).length === 0) && (
                        <div className="text-center py-8 text-gray-500">
                          <FlaskConical className="w-12 h-12 mx-auto mb-3 opacity-50" />
                          <p>No data collected yet for this experiment.</p>
                          <p className="text-sm">Data will appear once users interact with the variants.</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>

        {/* Info Section */}
        <div className="mt-8 p-6 bg-blue-50 rounded-lg border border-blue-200">
          <h3 className="font-semibold text-blue-900 mb-2">How A/B Testing Works</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Users are randomly assigned to variants when they first visit the site</li>
            <li>• Assignments are persisted in localStorage for consistent experience</li>
            <li>• Impressions are tracked when components are viewed</li>
            <li>• Conversions are tracked for specific actions (clicks, signups, etc.)</li>
            <li>• The "Leading" variant shows which has the highest conversion rate</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ABTestAdmin;
